import { Calculator } from "lucide-react";
import Lottie from "lottie-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { NodeProps } from "reactflow";
import { Dimmer, Grid, Loader } from "semantic-ui-react";
import {
  useCreateDrawing,
  useGetDrawingToolData,
  usePutDrawingToolDataToQueue,
  useSendErrorDetails,
  useUpdateDrawing
} from "../../api/drawingTool";
import { useCreateProcessGate, useUpdateProcessGate } from "../../api/processGate/processGate";
import calculatorLoading from "../../assets/animations/CalculatorLoading.json";
import { PopupButton, TitleActionBar } from "../../components";
import ConfirmModal from "../../components/confirmViewModal/ConfirmModal";
import ChangeRequestModal from "../../components/drawing-tool/ChangeRequestModal";
import DrawingTool from "../../components/drawing-tool/DrawingTool";
import { sortNodes } from "../../components/drawing-tool/utils";
import { Button as TailwindButton } from "../../components/ui/common/button";
import { DISABLE_REVIEW_BUTTON } from "../../config/constants";
import { MappingSourceType, MappingType } from "../../config/enums/MappingSourceType.enum";
import { ROLES } from "../../config/permission-maps";
import { errorView, successMessage } from "../../helpers/ErrorHandler";
import useUploadMappingImage from "../../hooks/useUploadMappingImage";
import { getUserRolesInLocal } from "../../utils/cacheStorage";
import { getStringInsideBrackets } from "../../utils/utils";
import CopyMappingModal from "./CopyMappingModal";
import DeleteMappingModal from "./DeleteMappingModal";
import ImportMappingModal from "./ImportMappingModal";
import DrawingErrorsModal from "./MappingErrorsModal";
import ProcessGateFormModal from "./UpdateNodes/ProcessGate/components/ProcessGateFormModal";
import UpdateNodeModal from "./UpdateNodes/UpdateNodeModal";
import { useDrawingInitialData } from "./hooks/useDrawingInitialData";
import { useMissingMappingData } from "./hooks/useMissingMappingData";
import { useRTCProvider } from "./hooks/useRTCProvider";

const MappingTool = () => {
  // Drawing States
  const [searchParams, setSearchParams] = useSearchParams();
  const paramId = searchParams.get("sourceId");

  const { mappingType: paramMappingType } = useParams<string>();
  const mappingType = paramMappingType as keyof typeof MappingSourceType;
  const mappingSourceType = MappingSourceType[mappingType];

  const [isVisibleCopyModal, setIsVisibleCopyModal] = useState(false);
  const [isVisibleDeleteModal, setIsVisibleDeleteModal] = useState(false);
  const [isVisibleImportModal, setIsVisibleImportModal] = useState(false);
  const [isVisibleEditModal, setIsVisibleEditModal] = useState<boolean>(false);
  const [isVisibleChangeRequestModal, setIsVisibleChangeRequestModal] = useState<boolean>(false);
  const [processGateNode, setProcessGateNode] = useState<NodeProps | null>();
  const [isProcessGateCreated, setIsProcessGateCreated] = useState<boolean>(false);
  const [productData, saveProductData] = useState<any>({});

  // Dropdown States
  const [searchKey, setSearchKey] = useState("");
  const [sourceId, setSourceId] = useState<string>("");
  const [calculateModalOpen, setCalculateModalOpen] = useState<boolean>(false);
  const [putDataToQueueErrors, setPutDataToQueueErrors] = useState<string[]>([]);
  const [calculateErrorMessage, setCalculateErrorMessage] = useState<Boolean>(false);
  const [enableLoading, setEnableLoading] = useState<Boolean>(false);
  const [ownerId, setOwnerId] = useState<any>("");
  const [selectProductDetails, setSelectProductDetails] = useState<any>(null);

  const navigate = useNavigate();

  useRTCProvider(paramId, () => {
    const sourceId = searchParams.get("sourceId");
    setSourceId(sourceId || "");
  });

  const { mutate: createProcessGateMutate } = useCreateProcessGate();

  const {
    ownerId: rawOwnerId,
    selectionData,
    salesUnitsById,
    isSourceLoading
  } = useDrawingInitialData({
    searchKey,
    sourceId,
    saveProductData,
    mappingSourceType: mappingSourceType
  });

  useEffect(() => {
    if (rawOwnerId !== undefined && rawOwnerId !== ownerId) {
      setOwnerId(rawOwnerId);
    }
  }, [rawOwnerId, ownerId]);

  const {
    data: drawingToolData,
    isLoading: isDrawingToolLoading,
    refetch: reFetchDrawingTool
  } = useGetDrawingToolData(mappingSourceType, sourceId, ownerId);

  const [missingNodeIds, setMissingNodeIds, checkForMissingMappingData] = useMissingMappingData();

  const [isLoadingImageUpload, uploadImage] = useUploadMappingImage(
    drawingToolData?.chartNodes || [],
    mappingSourceType
  );

  const { mutate: updateProcessGateAction } = useUpdateProcessGate();

  const { mutate: createDrawing, isLoading: isLoadingCreateDrawing } =
    useCreateDrawing(mappingSourceType);

  const { mutate: updateDrawing, isLoading: isLoadingUpdateDrawing } =
    useUpdateDrawing(mappingSourceType);

  const { isLoading: isLoadingPutDataToQueue, mutate: putDrawingToolDataToQueueAsync } =
    usePutDrawingToolDataToQueue(mappingSourceType);

  const { mutate: sendErrorDetails } = useSendErrorDetails(mappingSourceType);

  const saveDrawing = useCallback(
    (chartEdges: any, chartNodes: any, status: boolean = false, callBack?: () => void) => {
      setEnableLoading(status);
      // SHARED3 Fix: Use fresh drawingToolData from dependency, not stale closure
      if (drawingToolData?._id) {
        updateDrawing(
          {
            ...drawingToolData,
            chartEdges: chartEdges,
            chartNodes: chartNodes
          },
          {
            onSuccess: () => {
              reFetchDrawingTool();
              checkForMissingMappingData(chartNodes, () => {
                setMissingNodeIds([]);
              });
              setEnableLoading(false);
              // CRITICAL-4 Fix: Only call callBack if it's defined
              if (callBack) {
                callBack();
              }
            },
            // SHARED2 Fix: Add onError handler
            onError: (error: any) => {
              setEnableLoading(false);
              errorView(error?.msg || "Failed to save drawing");
            }
          }
        );
        return;
      }
      const newDrawing = {
        sourceId,
        ownerId,
        chartEdges: chartEdges,
        chartNodes: chartNodes
      };
      createDrawing(newDrawing, {
        onSuccess: () => {
          reFetchDrawingTool();
          checkForMissingMappingData(chartNodes, () => {
            setMissingNodeIds([]);
          });
          // CRITICAL-4 Fix: Only call callBack if it's defined
          if (callBack) {
            callBack();
          }
        },
        // SHARED2 Fix: Add onError handler
        onError: (error: any) => {
          setEnableLoading(false);
          errorView(error?.msg || "Failed to create drawing");
        }
      });
    },
    [
      drawingToolData,
      sourceId,
      ownerId,
      updateDrawing,
      createDrawing,
      reFetchDrawingTool,
      checkForMissingMappingData
    ]
  );

  const saveMappingBeforeCalculate = useCallback(
    (chartEdges: any, chartNodes: any) => {
      checkForMissingMappingData(chartNodes, errorMessages => {
        setCalculateModalOpen(true);
        if (errorMessages.length > 0) {
          setCalculateErrorMessage(true);
          return;
        }
        setCalculateErrorMessage(false);
        setPutDataToQueueErrors([]);
        saveDrawing(chartEdges, chartNodes, false);
      });
    },
    [missingNodeIds, saveDrawing, checkForMissingMappingData]
  );

  const navigateReview = () => {
    return navigate(
      `/mapping/review/${mappingSourceType}/${
        drawingToolData?._id
      }/${ownerId}/${sourceId}?isAverageProduct=${
        selectProductDetails?.isAverageProduct || false
      }&isEditable=${!drawingToolData?.lockStatus}`
    );
  };

  // Email handler
  const handleClickEmail = async () => {
    const companyName = localStorage.getItem("companyName");
    const mappingUrl = window.location.href;
    setCalculateModalOpen(false);

    if (!ownerId || !sourceId || !companyName || !mappingUrl) {
      return;
    }

    sendErrorDetails(
      {
        ownerId,
        sourceId,
        companyName,
        mappingUrl
      },
      {
        onSuccess: () => {
          setCalculateErrorMessage(false);
        },
        onError: error => {
          errorView(`Error sending details: ${error}`);
        }
      }
    );
  };

  const uploadMappingImage = () => {
    if (!sourceId) return;

    uploadImage(sourceId, ownerId, () => {
      if (mappingSourceType === MappingSourceType.ingredient) {
        navigate("/ingredient");
      } else {
        navigate(`/product-summary?salesUnitId=${sourceId}&productId=${ownerId}`);
      }
    });
  };

  const calculateHandleApprove = () => {
    putDrawingToolDataToQueueAsync(drawingToolData, {
      onError: (error: any) => {
        const checkValidationError = error?.response?.data?.data;
        if (
          checkValidationError &&
          Array.isArray(checkValidationError) &&
          checkValidationError.length > 0
        ) {
          setPutDataToQueueErrors(checkValidationError);
          setCalculateModalOpen(false);
          return;
        }
        setCalculateErrorMessage(true);
        setCalculateModalOpen(true);
      },
      onSuccess: () => {
        setCalculateModalOpen(false);
        setCalculateErrorMessage(false);

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            uploadMappingImage();
          });
        });
      }
    });
  };

  const toggleProcessGateModel = (node: NodeProps | null, isCreated: boolean) => {
    setIsProcessGateCreated(isCreated);
    setProcessGateNode(node);
  };

  //update process gate data
  const updateProcessGate = (processGateName: string, newNode: NodeProps) => {
    const facilityId = newNode.data.facilityId;
    let processGate = {
      processGateId: processGateNode?.data.reference.processGateId,
      processGateName,
      facilityId,
      ownerId,
      sourceId,
      userCompletedStage: "RECIPE"
    };
    updateProcessGateAction(processGate, {
      onSuccess(data) {
        updateProcessGateData(newNode, {
          processGateId: data._id,
          ownerId: data?.ownerId,
          sourceId: data?.sourceId
        });
        toggleProcessGateModel(null, false);
      }
    });
  };

  const updateProcessGateData = (nodeItem: any, processData: any) => {
    const updatedNode = {
      ...nodeItem,
      data: {
        ...nodeItem?.data,
        reference: processData
      }
    };
    const chartNodes = drawingToolData?.chartNodes || [];
    const chartEdges = drawingToolData?.chartEdges || [];
    const updatedChartNodes = [...chartNodes.filter(n => n.id !== nodeItem.id), updatedNode].sort(
      sortNodes
    );
    saveDrawing(chartEdges, updatedChartNodes, false, () => {
      successMessage("Update process gate successfully");
    });
  };

  //create process gate data
  const createProcessGate = (processGateName: string, newNode: NodeProps) => {
    const facilityId = newNode.data.facilityId;
    let processGate = {
      processGateName,
      facilityId,
      ownerId,
      sourceId,
      userCompletedStage: "RECIPE",
      processing: [],
      internalTransportation: [],
      internalStorage: [],
      packaging: []
    };
    createProcessGateMutate(processGate, {
      onSuccess(data) {
        saveProcessGateData(newNode, {
          processGateId: data._id,
          ownerId: data?.ownerId,
          sourceId: data?.sourceId
        });
        toggleProcessGateModel(null, false);
      }
    });
  };

  //save transport node data
  const saveProcessGateData = (nodeItem: any, processData: any) => {
    const updatedNode = {
      ...nodeItem,
      data: {
        ...nodeItem?.data,
        reference: processData
      }
    };
    const chartNodes = drawingToolData?.chartNodes || [];
    const chartEdges = drawingToolData?.chartEdges || [];
    const updatedChartNodes = [...chartNodes, updatedNode].sort(sortNodes);
    saveDrawing(chartEdges, updatedChartNodes, false, () => {
      successMessage("Update process gate successfully");
    });
  };

  // Data for dropdown
  const updateSourceId = (data: any) => {
    const id = data.value as string;
    const getSelectedProduct = salesUnitsById[id];
    setSelectProductDetails(getSelectedProduct);
    setSourceId(id);
    searchParams.set("sourceId", id);
    setSearchParams(searchParams);
  };

  const cancel = () => {
    setCalculateModalOpen(false);
    setCalculateErrorMessage(false);
  };

  const checkDrawingButtonStatus = useMemo(() => getUserRolesInLocal(), [getUserRolesInLocal]);

  const checkUserIsSupplier = checkDrawingButtonStatus?.[0] === ROLES.supplier;

  const calculationTile = checkUserIsSupplier
    ? "Are you sure you have entered all the data for complete calculation?"
    : "Are you sure you have entered all the data for calculation?";

  if (!sourceId && mappingType === MappingSourceType.product) {
    return (
      <>
        <TitleActionBar
          title={"Product Name"}
          currentData={selectionData}
          defaultValue={sourceId}
          handleChangeState={(e: any, data: any) => {
            updateSourceId(data);
          }}
          onSearchChange={(e: any, data: any) => {
            setSearchKey(data.searchQuery);
          }}
          loading={isSourceLoading}
          search
        />
        <div className="product-empty-description">
          <h3>
            Please select the product or create the product if product is not available from the
            dropdown.
          </h3>
        </div>
      </>
    );
  }

  // Loading state check
  if (
    isDrawingToolLoading ||
    isLoadingCreateDrawing ||
    (isLoadingUpdateDrawing && enableLoading) ||
    isLoadingImageUpload
  ) {
    return (
      <Dimmer active>
        <Loader content="Loading" />
      </Dimmer>
    );
  }

  if (isLoadingPutDataToQueue) {
    return (
      <Dimmer active>
        <div className="loading-calculation">
          <Lottie animationData={calculatorLoading} loop={true} />
        </div>
      </Dimmer>
    );
  }
  const isIngredientMap = mappingType === MappingSourceType.ingredient;
  return (
    <>
      <DrawingTool
        sourceId={sourceId}
        ownerId={ownerId}
        lockStatus={drawingToolData?.lockStatus}
        chartNodesData={drawingToolData?.chartNodes || []}
        chartEdgesData={drawingToolData?.chartEdges || []}
        saveDrawing={saveDrawing}
        actionBar={
          mappingType === MappingSourceType.product ? (
            <TitleActionBar
              title={"Product Name"}
              currentData={selectionData}
              defaultValue={sourceId}
              handleChangeState={(e: any, data: any) => {
                updateSourceId(data);
              }}
              onSearchChange={(e: any, data: any) => {
                setSearchKey(data.searchQuery);
              }}
              loading={isSourceLoading}
              search
            />
          ) : null
        }
        updateNodeModal={() => <UpdateNodeModal sourceId={sourceId} ownerId={ownerId} />}
        actionButtons={(chartNodes: any, chartEdges: any) => {
          return (
            <Grid className="mapping-action-buttons">
              <Grid.Row>
                {!drawingToolData?._id && !isIngredientMap && (
                  <PopupButton
                    tooltip="Import Existing Mapping."
                    buttonClass="drawing-button"
                    buttonName="Import"
                    onClick={() => {
                      setIsVisibleImportModal(true);
                    }}
                  />
                )}
                <PopupButton
                  tooltip="Delete Mapping."
                  buttonClass="drawing-button"
                  buttonName="Delete"
                  onClick={() => {
                    setIsVisibleDeleteModal(true);
                  }}
                />
                {drawingToolData?._id && mappingSourceType !== MappingSourceType.ingredient ? (
                  <PopupButton
                    tooltip="Copy Mapping."
                    buttonClass="drawing-button"
                    buttonName="Duplicate"
                    onClick={() => {
                      setIsVisibleCopyModal(true);
                    }}
                  />
                ) : null}
                {drawingToolData?.lockStatus ? (
                  <>
                    <PopupButton
                      tooltip="Edit Mapping Data."
                      buttonClass="drawing-button"
                      buttonName="Edit Mapping"
                      onClick={() => {
                        setIsVisibleEditModal(true);
                      }}
                    />
                    {!DISABLE_REVIEW_BUTTON ? (
                      <PopupButton
                        tooltip="Review Mapping Data."
                        buttonClass="drawing-button"
                        buttonName={"Review"}
                        onClick={() => {
                          navigateReview();
                        }}
                      />
                    ) : null}
                  </>
                ) : (
                  <>
                    <PopupButton
                      tooltip="Save Mapping."
                      buttonClass="drawing-button"
                      buttonName="Save"
                      onClick={() => {
                        saveDrawing(chartEdges, chartNodes, true, () => {
                          successMessage("Save successfully");
                        });
                      }}
                    />
                    <PopupButton
                      tooltip="Calculate Mapping Data."
                      buttonClass="drawing-button"
                      buttonName={checkUserIsSupplier ? "Complete" : "Calculate"}
                      onClick={() => {
                        saveMappingBeforeCalculate(chartEdges, chartNodes);
                      }}
                    />
                    {!DISABLE_REVIEW_BUTTON ? (
                      <PopupButton
                        tooltip="Review Mapping Data."
                        buttonClass="drawing-button"
                        buttonName={"Review"}
                        onClick={() => {
                          navigateReview();
                        }}
                      />
                    ) : null}
                  </>
                )}
              </Grid.Row>
            </Grid>
          );
        }}
        contextProps={{
          productData,
          mappingSourceType,
          missingNodeIds,
          toggleProcessGateModel,
          saveProductData,
          setMissingNodeIds
        }}
      />
      <ConfirmModal
        viewModal={calculateModalOpen}
        closeModal={() => {
          setCalculateModalOpen(false);
          setCalculateErrorMessage(false);
        }}
        cancel={() => cancel()}
        approve={calculateHandleApprove}
        title={"Calculation Mapping Data"}
        confirmText={checkUserIsSupplier ? "Complete" : "Calculate"}
        variant="default"
        icon={<Calculator className="tw-h-5 tw-w-5" />}
        subTitle={
          calculateErrorMessage
            ? "Are you sure you have entered all the required data for calculations? Please check the data again or contact Nature Preserve."
            : calculationTile
        }
        otherConditionCheck={calculateErrorMessage}
        otherConditionBottomButton={
          <div className="tw-flex tw-w-full tw-flex-wrap tw-justify-end tw-gap-2">
            <TailwindButton type="button" variant="outline" onClick={cancel}>
              Check Data
            </TailwindButton>
            <TailwindButton type="button" onClick={handleClickEmail}>
              Send Details About the Issue
            </TailwindButton>
          </div>
        }
      />
      <ConfirmModal
        viewModal={isVisibleEditModal}
        closeModal={() => setIsVisibleEditModal(false)}
        cancel={() => {
          setIsVisibleEditModal(false);
        }}
        approve={() => {
          setIsVisibleEditModal(false);
          setIsVisibleChangeRequestModal(true);
        }}
        title="Edit Mapping"
        subTitle="Are you sure you want to edit this mapping?"
        confirmText="Confirm"
        variant="default"
      />
      {isVisibleCopyModal ? (
        <CopyMappingModal
          drawingId={drawingToolData?._id}
          closeModal={() => {
            setIsVisibleCopyModal(false);
          }}
          onSuccessCopy={(sourceId: any) => {
            setSourceId(sourceId);
          }}
        />
      ) : null}
      <DrawingErrorsModal
        errors={putDataToQueueErrors}
        setPutDataToQueueErrors={setPutDataToQueueErrors}
      />
      {processGateNode ? (
        <ProcessGateFormModal
          setProcessGateNode={toggleProcessGateModel}
          newNode={processGateNode}
          edit={isProcessGateCreated}
          processGateName={getStringInsideBrackets(processGateNode?.data?.label)}
          updateProcessGate={updateProcessGate}
          createProcessGate={createProcessGate}
          chartNodes={drawingToolData?.chartNodes || []}
        />
      ) : null}
      {isVisibleDeleteModal ? (
        <DeleteMappingModal
          selectionData={selectionData}
          sourceId={sourceId}
          closeModal={(isDone: boolean | undefined) => {
            setIsVisibleDeleteModal(false);
            if (isDone) {
              reFetchDrawingTool();
            }
          }}
          drawingId={drawingToolData?._id}
          mappingSourceType={mappingSourceType}
        />
      ) : null}

      {isVisibleImportModal ? (
        <ImportMappingModal
          drawingId={drawingToolData?._id}
          ownerId={ownerId}
          sourceId={sourceId}
          mappingSourceType={mappingSourceType}
          closeModal={() => {
            setIsVisibleImportModal(false);
          }}
          onSuccessCopy={(sourceId: any) => {
            setSourceId(sourceId);
            reFetchDrawingTool();
          }}
        />
      ) : null}

      {isVisibleChangeRequestModal ? (
        <ChangeRequestModal
          drawingId={drawingToolData?._id}
          ownerId={ownerId}
          sourceId={sourceId}
          mappingSourceType={MappingType[mappingSourceType]}
          closeModal={() => {
            setIsVisibleChangeRequestModal(false);
          }}
          onSuccessEdit={() => {
            reFetchDrawingTool();
          }}
        />
      ) : null}
    </>
  );
};

export default MappingTool;
